import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import formatDateTime from "components/Shared/formatDateTime"
import TableShimmer from "components/Shared/TableShimmer"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Badge, Button, Card, CardBody, Col, Row, Table } from "reactstrap"
import { getPosBranches, syncPosBranch } from "store/actions/posAction"
import { showAlert } from "store/reducers/alertReducer"

const BranchSync = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Branch Sync | Namq")

  const dispatch = useDispatch()
  const [selectedId, setSelectedId] = useState(null)

  const {
    loading,
    isLoading,
    itemsPerPage,
    currentPage,
    totalPages,
    totalCount,
    posBranches,
  } = useSelector(state => state.POS)

  const navigate = useNavigate()
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    status: 2,
    sortOrder: 1,
  })

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },

    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const { userRole, accountType } = useSelector(state => state.Login)

  const [permissions, setPermissions] = useState({
    list: null,
    edit: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      29,
      "branch_sync_1",
      accountType
    )

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      29,
      "branch_sync_3",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  useEffect(() => {
    fetchPOSBranch()
  }, [])

  const fetchPOSBranch = (page = 1) => {
    const payload = {
      perPage: 10,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getPosBranches(payload))
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleFetchPosBranches = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    dispatch(getPosBranches(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/branch-sync?page=${page}`)
        }
      })
      .catch(error => {})
  }

  const handleSearch = () => {
    handleFetchPosBranches(1) // Always search from page 1
  }

  const onPageChange = page => {
    handleFetchPosBranches(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
    setSearchTerm("")
    const payload = {
      perPage: 10,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getPosBranches(payload))
    navigate(`/branch-sync?page=1`)
  }

  const handleBranchSync = (id, countryCode) => {
    const payload = {
      countryCode,
      id,
    }
    dispatch(syncPosBranch(payload))
      .then(action => {
        const { statusCode, status, success, msg, message } = action?.payload
        if ((statusCode === 200 && status) || success) {
          dispatch(showAlert({ text: msg || message, type: "success" }))
          setSelectedId(null)
          handleFetchPosBranches(currentPage)
        } else {
          dispatch(showAlert({ text: msg || message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown cardTitle={t("POS Branches")} />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                />

                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : posBranches.length === 0 ? (
                    <p className="text-center my-2">{t("No data found")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Branch Name")}</th>
                            <th>{t("Branch Reference")}</th>
                            <th>{t("Country Code")}</th>
                            <th>{t("Organization Id")}</th>
                            <th>{t("Terminal Group Id")}</th>
                            <th>{t("Webhook URL")}</th>
                            <th>{t("Payment Type Id")}</th>
                            <th>{t("Last Updated")}</th>
                            {permissions.edit && <th>{t("Action")}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {posBranches.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(`/branch-sync/details?id=${item._id}`)
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </th>
                              <td>
                                {language === "sa" ? item.nameAr : item.name}
                              </td>
                              <td>{item.branchId || "-"}</td>
                              <td>{item.country}</td>
                              <td>
                                {item?.posSettings?.organizationId ? (
                                  <Badge className="bg-success">
                                    {t("Connected")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Not Connected")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {item?.posSettings?.terminalGroupId ? (
                                  <Badge className="bg-success">
                                    {t("Connected")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Not Connected")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {item?.posSettings?.webhookUrl ? (
                                  <Badge className="bg-success">
                                    {t("Setup")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Not Setup")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {item?.posSettings?.paymentTypeId ? (
                                  <Badge className="bg-success">
                                    {t("Connected")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Not Connected")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {formatDateTime(item?.posSettings?.updatedAt)}
                              </td>
                              {permissions.edit && (
                                <td
                                  onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                  }}
                                >
                                  <Button
                                    color="primary"
                                    disabled={
                                      selectedId === item._id && isLoading
                                    }
                                    onClick={() => {
                                      handleBranchSync(item._id, item.country)
                                      setSelectedId(item._id)
                                    }}
                                  >
                                    {t("Sync")}
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
                {!loading && posBranches.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={posBranches}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default BranchSync
